import { useCallback, useRef } from 'react';

import { StripeCardTypes } from '@givebacks/api/core';
import { useForm } from '@givebacks/formy';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { Box, CheckboxCard, Group, Input, Stack, TextInput, Title } from 'ui';
import { hasLength, isNotEmpty } from 'ui/Form';

import { useLoadedStripe } from '../../../../hooks/use-load-stripe/useLoadedStripe';
import type { StripeCardFormTransformedValues, StripeCardFormValues } from './StripeCard';
import classes from './StripeCardInfoForm.module.css';
import { translateCardBrand } from './utils/translate-card-brand';

/** Generic stripe card error */
const CARD_ERROR = 'A valid credit card is required';

const CARD_OPTIONS = {
  disableLink: true,
  hidePostalCode: true,
  style: {
    base: {
      fontFamily: 'Poppins, sans-serif',
    },
    invalid: {
      iconColor: '#d04b6e',
      color: '#d04b6e',
    },
  },
};

type StripeCardInfoFormProps = {
  title?: string;
  withSaveCardCheckbox?: boolean;
};

const CheckoutForm = ({ withSaveCardCheckbox = false }: { withSaveCardCheckbox?: boolean }) => {
  const isTokenizingRef = useRef(false);

  const stripe = useStripe();
  const elements = useElements();

  const onComplete = useCallback(
    async (zipCode: string) => {
      if (!stripe || !elements) {
        throw new Error('Stripe is not loaded');
      }
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error('Card element is not loaded');
      }

      const { token, error } = await stripe.createToken(cardElement, {
        address_zip: zipCode,
      });
      if (error || !token || !token.card) {
        throw new Error(error?.message ?? 'Invalid card');
      }

      return {
        token: token.id,
        card_type: token.card.brand as StripeCardTypes,
      };
    },
    [elements, stripe]
  );

  const cardForm = useForm<StripeCardFormValues, StripeCardFormTransformedValues>(
    {
      initialValues: {
        token: null,
        shouldSaveCard: false,
        stripeComplete: false,
        card_type: '',
        zipCode: undefined,
      },
      validate: {
        stripeComplete: isNotEmpty(CARD_ERROR),
        zipCode: hasLength({ min: 5 }, 'A valid zip code is required'),
      },
      transformValues: async (values) => {
        if (isTokenizingRef.current) {
          return {
            error: { message: CARD_ERROR },
          };
        }
        if (!values.stripeComplete || !values.zipCode || !cardForm.isValid()) {
          return {
            error: { message: CARD_ERROR },
          };
        }
        isTokenizingRef.current = true;
        try {
          const { token, card_type } = await onComplete(values.zipCode);
          cardForm.setFieldValue('card_type', card_type);
          return {
            token: token,
            card_type: card_type,
            shouldSaveCard: values.shouldSaveCard,
          };
        } catch {
          cardForm.setFieldError('stripeComplete', 'A valid credit card is required');
          return {
            error: { message: CARD_ERROR },
          };
        } finally {
          isTokenizingRef.current = false;
        }
      },
      validateInputOnBlur: true,
    },
    {
      id: 'stripe_credit_card',
    }
  );

  const onChange = useCallback(
    (event: StripeCardElementChangeEvent) => {
      // When auto fill includes the zip code,
      // we'll set the zip code to the value from the browser
      if (event.value?.postalCode) {
        cardForm.setFieldValue('zipCode', event.value?.postalCode);
      }

      if (event.brand) {
        cardForm.setFieldValue('card_type', translateCardBrand(event.brand));
      }

      if (event.complete) {
        cardForm.setFieldValue('stripeComplete', true);
      } else {
        cardForm.setFieldValue('stripeComplete', false);
        cardForm.setFieldValue('token', null);
      }
    },
    [cardForm]
  );

  const handleStripeCardBlur = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { onBlur } = cardForm.getInputProps('stripeComplete');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call -- OnBlur from GetInputProps is not strictly defined
    return onBlur?.();
  }, [cardForm]);

  return (
    <Stack ref={cardForm.formRef}>
      <Input.Wrapper error={cardForm.errors.stripeComplete}>
        <Box
          className={classes.stripePaymentElement}
          mod={{
            error: !!cardForm.errors.stripeComplete,
          }}
        >
          <CardElement
            id="payment-element"
            options={CARD_OPTIONS}
            onChange={onChange}
            onBlur={handleStripeCardBlur}
          />
        </Box>
      </Input.Wrapper>

      <TextInput
        placeholder="Zip"
        name="zip_code"
        autoComplete="shipping postal-code"
        onKeyDown={(event) => {
          const letterRegex = new RegExp('^[a-zA-Z]{1}$');
          const letter = letterRegex.test(event.key);
          if (letter || event.code == 'Space') {
            event.preventDefault();
          }
        }}
        {...cardForm.getInputProps('zipCode')}
      />

      {withSaveCardCheckbox && (
        <CheckboxCard
          description="Save this card for future payments"
          {...cardForm.getInputProps('shouldSaveCard')}
        />
      )}
    </Stack>
  );
};

const StripeCardInfoForm = ({
  title = 'Debit or Credit Card',
  withSaveCardCheckbox = false,
}: StripeCardInfoFormProps) => {
  const stripe = useLoadedStripe();

  return (
    <Stack>
      {title && (
        <Group gap={4}>
          <Title order={4}>{title}</Title>
          <Box c="cerise.7" fz={'sm'} fw={500}>
            *
          </Box>
        </Group>
      )}
      {/* A React Context Provider that passes down a secure, preconfigured connection to Stripe’s frontend SDK so all nested payment components can talk to Stripe safely and consistently. */}
      <Elements stripe={stripe}>
        <CheckoutForm withSaveCardCheckbox={withSaveCardCheckbox} />
      </Elements>
    </Stack>
  );
};

export { StripeCardInfoForm };
